"use client";

import { useEffect, useState } from "react";
import { useMessages } from "next-intl";
import { RichText } from "@sitecore-content-sdk/nextjs";
import { convertToEmbedUrl } from "lib/utils";

interface VideoIframeProps {
  src: string;
  title?: string;
  className?: string;
  dataTestId?: string;
  ariaLabel?: string;
  isPreviewMode?: boolean;
}

const VideoIframe = ({
  src,
  title = "Video",
  className,
  dataTestId = "youtube-video",
  ariaLabel = "YouTube video",
  isPreviewMode = false,
}: VideoIframeProps) => {
  const hasYoutubeVideo = src?.includes("youtube") || src?.includes("youtu.be");
  const [hasConsent, setHasConsent] = useState(!hasYoutubeVideo);
  const [isConsentCheckDone, setIsConsentCheckDone] =
    useState(!hasYoutubeVideo);

  useEffect(() => {
    const checkConsent = () => {
      if (isPreviewMode) {
        setHasConsent(true);
        setIsConsentCheckDone(true);
        return;
      }
      if (typeof window !== "undefined" && window.OptanonActiveGroups) {
        const consent = window.OptanonActiveGroups.includes("C0007");
        if (hasYoutubeVideo) setHasConsent(consent);
        if (!isConsentCheckDone) setIsConsentCheckDone(true);
      }
    };

    setTimeout(checkConsent);

    const handleConsentChange = () => checkConsent();
    window.addEventListener("OneTrustGroupsUpdated", handleConsentChange);

    return () => {
      window.removeEventListener("OneTrustGroupsUpdated", handleConsentChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const embedSrc = convertToEmbedUrl(src.toString());
  const messages = useMessages();
  const cookieErrorTitle = messages["youtube-cookie-title"]
    ? messages["youtube-cookie-title"]
    : "YouTube cookie consent required";
  const cookieErrorDescription = messages["youtube-cookie-description"]
    ? messages["youtube-cookie-description"]
    : "Please accept marketing cookies to view this video.";
  const cookieErrorCTAText = messages["youtube-cookie-cta-text"]
    ? messages["youtube-cookie-cta-text"]
    : "Update Cookie Preferences";
  const cookieErrorBottomContent = (
    messages["youtube-cookie-bottom-content"]
      ? messages["youtube-cookie-bottom-content"]
      : ""
  )
    ?.replace("{##youtubeURL##}", embedSrc)
    ?.replaceAll(String.raw`\"`, '"');

  return (
    <div
      className={`relative w-full bg-no-repeat bg-cover bg-center overflow-hidden
      ${!isConsentCheckDone || hasConsent ? "bg-current pb-[56.25%]" : ""} ${
        !hasConsent && isConsentCheckDone ? "bg-black lg:pb-[56.25%]" : ""
      } `}
    >
      {isConsentCheckDone && (
        <div
          className={`lg:absolute lg:z-1 h-full flex w-full bg-black px-3.75 pt-1.25 pb-2.5 ${
            hasConsent ? "hidden" : ""
          }`}
        >
          <div className="flex flex-col justify-between grow">
            <div>
              <h2 className="text-white text-[20px] mt-3.75 mb-1.25 font-medium leading-[1.1] pb-1.25">
                {cookieErrorTitle}
              </h2>
              <div className="text-white text-base [&_p]:tracking-normal! richtext-content [&_p]:mb-1.25!">
                <RichText field={{ value: cookieErrorDescription }} />
              </div>
            </div>
            <div className="flex justify-center min-h-12.5 items-center">
              <button
                type="button"
                id="ot-sdk-btn"
                className="ot-sdk-show-settings onetrust-yt-btn overflow-visible normal-case font-[inherit] text-ecolab-blue! border-2 border-ecolab-blue! text-[14px]! font-bold! rounded-[20px]! bg-white! [&:hover]:underline [&:hover]:text-cookie-btn-blue!"
              >
                {cookieErrorCTAText}
              </button>
            </div>
            <div className="text-white text-base richtext-content [&_p]:tracking-normal! [&_a]:text-white [&_a:hover]:text-link-blue-dark-bg! [&_a:focus]:text-link-blue-dark-bg! [&_a]:underline [&_p]:mb-1.25!">
              <RichText field={{ value: cookieErrorBottomContent }} />
            </div>
          </div>
        </div>
      )}

      {hasConsent && (
        <iframe
          src={embedSrc}
          title={title}
          data-testid={dataTestId}
          allowFullScreen
          className={`absolute inset-0 z-5 w-full h-full ${className}`}
          aria-label={ariaLabel}
        />
      )}
    </div>
  );
};

export default VideoIframe;
